import { drawNeedsAttention } from './drawNeedsAttention';
import { useTheme } from '@src/theme/useTheme';
import { useAppSelector } from '@src/state/hooks';
import { mainSiteUrl } from '@src/routing/boxDomainNavigation';
import { ThemeIcon } from '@src/theme/ThemeIcon';
import type { ThemeIconName } from '@src/theme/types';
import { useMessageInbox } from './MessageInbox';
import { NotificationBadge } from '@src/components/NotificationBadge/NotificationBadge';
import { useEffect, useRef, useState } from 'react';
import { Menu } from '@src/components/Menu/Menu';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { MoreIcon } from '@src/components/icons/MoreIcon';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { BoxInfo } from '@lub0v/santa-utils';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import {
  boxMainPath,
  boxExchangePath,
  boxMessengerPath,
  boxOrganizerPath,
  boxSettingsPath,
  type BoxRoute,
} from '@src/routing/routes';
import './MobileBoxNav.css';

export function MobileBoxNav({
  info,
  view,
  placement = 'bottom',
}: {
  info: BoxInfo;
  view: BoxRoute['view'];
  placement?: 'bottom' | 'inline' | 'hanging';
}) {
  const { t } = useTranslation();
  const { season } = useTheme();
  const separateSettings = placement === 'inline' && season === 'cherry-blossom';
  const hasUnrevealedGiftee = useAppSelector(({ box }) =>
    box.key === info.box.key &&
    info.isParticipant && info.isActiveDraw && !info.box.isArchived &&
    Boolean(info.wardId) && box.card?.id === info.cardId &&
    box.card?.isWardKnown === false,
  );
  const needsDrawAttention = useAppSelector(({ box }) =>
    box.key === info.box.key && info.isAdmin && drawNeedsAttention(info, box.cards),
  );
  const hasUnopenedGift = (info.isAdmin || info.isParticipant) && Boolean(info.gift && !info.gift.openedAt);
  const { summaries } = useMessageInbox();
  const unread = Object.values(summaries).reduce(
    (count, summary) => count + (summary?.unread ?? 0),
    0,
  );
  const navigate = useNavigate();
  const moreRef = useRef<HTMLButtonElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [search] = useSearchParams();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const [hidden, setHidden] = useState(false);
  const routeKey = `${location.pathname}${location.search}:${info.isAdmin}:${info.isParticipant}:${placement}`;
  const [previousRoute, setPreviousRoute] = useState(routeKey);
  if (routeKey !== previousRoute) {
    setPreviousRoute(routeKey);
    setHidden(false);
  }

  useEffect(() => {
    if (placement !== 'bottom') return;
    const nav = navRef.current;
    const layout = nav?.closest('.mobile-box-layout');
    if (!nav || !layout) return;
    const positions = new WeakMap<Element, number>();
    for (const element of layout.querySelectorAll(
      '.mobile-box-layout__scroll, .messenger__threads',
    )) {
      positions.set(element, element.scrollTop);
    }
    let downwardDistance = 0;
    const onScroll = (event: Event) => {
      const target = event.target;
      if (
        !(target instanceof HTMLElement) ||
        !target.matches('.mobile-box-layout__scroll, .messenger__threads')
      )
        return;
      // Clamp rubber-band overscroll so bouncing at an edge does not change direction.
      const top = Math.max(
        0,
        Math.min(target.scrollTop, target.scrollHeight - target.clientHeight),
      );
      const delta = top - (positions.get(target) ?? 0);
      positions.set(target, top);
      if (moreOpen || nav.querySelector(':focus-visible') || top <= 0) {
        downwardDistance = 0;
        setHidden(false);
      } else if (delta < 0) {
        downwardDistance = 0;
        setHidden(false);
      } else if (delta > 0) {
        downwardDistance += delta;
        if (downwardDistance >= nav.offsetHeight) setHidden(true);
      }
    };
    layout.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => layout.removeEventListener('scroll', onScroll, true);
  }, [location.pathname, location.search, moreOpen, info.isAdmin, info.isParticipant, placement]);
  if (
    (!info.isAdmin && !info.isParticipant) ||
    (placement === 'bottom' && view === 'messenger' && search.has('selected'))
  )
    return null;
  const hasMore = placement !== 'inline';
  const key = info.box.key;
  const settingsSelected = view === 'organizer' && ['settings', 'appearance', 'questionnaires', 'actions'].includes(search.get('tab') ?? '');
  const current = settingsSelected ? (separateSettings ? 'settings' : 'organizer') : ['exchange', 'ward', 'card', 'card-edit'].includes(view)
    ? 'exchange'
    : view === 'settings' && hasMore
      ? 'organizer'
      : view === 'home'
        ? 'main'
        : view;
  const items = [
    {
      id: 'main',
      href: boxMainPath(key),
      label: t('game.nav.main'),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m3 10 9-7 9 7M5 9v12h5v-7h4v7h5V9" />
        </svg>
      ),
    },
    ...(info.isParticipant || info.isAdmin
      ? [
          {
            id: 'exchange',
            href: boxExchangePath(key),
            label: t('game.nav.exchange'),
            icon: <GiftIcon />,
          },
        ]
      : []),
    {
      id: 'messenger',
      href: boxMessengerPath(key),
      label: t('game.nav.messenger'),
      icon: <MessageIcon />,
    },
    ...((placement === 'inline' || placement === 'bottom') && info.isAdmin
      ? [
          {
            id: 'organizer',
            href: boxOrganizerPath(key),
            label: t('game.nav.organizer'),
            icon: separateSettings ? <ThemeIcon name="nav-organizer" fallback={<UsersIcon />} /> : <ThemeIcon name="nav-settings" fallback={<BoxSettingsIcon />} />,
          },
          ...(separateSettings ? [{
            id: 'settings',
            href: boxSettingsPath(key),
            label: t('game.nav.settings'),
            icon: <ThemeIcon name="nav-settings" fallback={<BoxSettingsIcon />} />,
          }] : []),
        ]
      : []),
  ];
  const activeIndex =
    moreOpen || (hasMore && current === 'organizer' && !items.some(item => item.id === 'organizer'))
      ? items.length
      : Math.max(
          0,
          items.findIndex((item) => item.id === current),
        );
  return (
    <nav
      ref={navRef}
      className={`mobile-box-nav mobile-box-nav--${placement}${hidden ? ' mobile-box-nav--hidden' : ''}`}
      onFocusCapture={() => setHidden(false)}
      aria-label={t('game.nav.navigation')}
      data-testid="mobile-box-nav"
      data-active-index={activeIndex}
      data-item-count={items.length + (hasMore ? 1 : 0)}
    >
      <span className="mobile-box-nav__indicator" aria-hidden="true" />
      {items.map((item) => (
        <Link
          key={item.id}
          data-nav-item={item.id}
          to={item.href}
          className="mobile-box-nav__item"
          aria-current={current === item.id ? 'page' : undefined}
          aria-label={
            item.id === 'organizer' && needsDrawAttention
              ? `${item.label}, ${t('game.draw.needsAttention')}`
              : item.id === 'messenger' && unread > 0
              ? `${item.label}, ${t('game.messenger.unreadCount', { count: unread })}`
              : item.id === 'exchange' && hasUnrevealedGiftee
                ? `${item.label}, ${t('game.header.unrevealedGiftee')}`
                : item.id === 'main' && hasUnopenedGift
                  ? `${item.label}, ${t('game.header.unopenedGift')}`
                  : item.label
          }
          title={item.label}
        >
          <span className="mobile-box-nav__icon" aria-hidden="true">
            <ThemeIcon name={(item.id === 'organizer' && !separateSettings ? 'nav-settings' : `nav-${item.id}`) as ThemeIconName} fallback={item.icon} />
            {item.id === 'organizer' && needsDrawAttention && (
              <span className="mobile-box-nav__notification-dot" data-testid="game-management-draw-attention" />
            )}
            {item.id === 'exchange' && hasUnrevealedGiftee && (
              <span className="mobile-box-nav__notification-dot" data-testid="game-exchange-unrevealed" />
            )}
            {item.id === 'main' && hasUnopenedGift && (
              <span className="mobile-box-nav__notification-dot" data-testid="game-main-unopened-gift" />
            )}
            {item.id === 'messenger' && unread > 0 && (
              <NotificationBadge
                count={unread}
                className="mobile-box-nav__unread"
                testId="game-messages-unread"
              />
            )}
          </span>
          <span>{item.label}</span>
        </Link>
      ))}
      {hasMore && (
        <>
          <button
            type="button"
            ref={moreRef}
            className="mobile-box-nav__item mobile-box-nav__more"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-current={current === 'organizer' && !items.some(item => item.id === 'organizer') ? 'page' : undefined}
            onClick={() => setMoreOpen(!moreOpen)}
            aria-label={t('game.nav.more')}
            title={t('game.nav.more')}
            data-testid="mobile-box-more"
          >
            <span className="mobile-box-nav__icon" aria-hidden="true">
              <ThemeIcon name="nav-more" fallback={<MoreIcon />} />
            </span>
            <span>{t('game.nav.more')}</span>
          </button>
          <Menu
            anchorRef={moreRef}
            open={moreOpen}
            onOpenChange={setMoreOpen}
            placement={placement === 'bottom' ? 'top' : 'bottom'}
            label={t('game.nav.more')}
            testId="mobile-box-more-menu"
            items={[
              ...(info.isAdmin && placement !== 'bottom'
                ? [
                    {
                      id: 'organizer',
                      onSelect: () => navigate(boxOrganizerPath(key)),
                      label: t('game.nav.organizer'),
                      icon: separateSettings ? <ThemeIcon name="nav-organizer" fallback={<UsersIcon />} /> : <ThemeIcon name="nav-settings" fallback={<BoxSettingsIcon />} />,
                    },

                  ]
                : []),
              {
                id: 'site-home',
                label: t('game.nav.siteHome'),
                icon: <ThemeIcon name="nav-main" fallback={items[0].icon} />,
                onSelect: () => window.location.assign(mainSiteUrl('/')),
              },
            ]}
          />
        </>
      )}
    </nav>
  );
}

function BoxSettingsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h2m6 0h8M4 17h8m6 0h2" />
      <circle cx="9" cy="7" r="3" />
      <circle cx="15" cy="17" r="3" />
    </svg>
  );
}
